import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type NetworkStatusValue = {
  online: boolean;
};

const NetworkStatusContext = createContext<NetworkStatusValue | null>(null);

function readOnline(): boolean {
  if (typeof navigator === "undefined") {
    return true;
  }
  return navigator.onLine;
}

export function NetworkStatusProvider({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState(readOnline);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const value = useMemo(() => ({ online }), [online]);

  return (
    <NetworkStatusContext.Provider value={value}>
      {children}
    </NetworkStatusContext.Provider>
  );
}

export function useNetworkStatus(): NetworkStatusValue {
  const value = useContext(NetworkStatusContext);
  if (!value) {
    throw new Error("useNetworkStatus must be used within NetworkStatusProvider");
  }
  return value;
}
